// src/components/Contract.js
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../../../Supabase/SupabaseClient";
import jsPDF from "jspdf";
import Footer from "../../../Components/Footer/Footer";
import './Contract.css';

const WHATSAPP_SOLE = "5493816818108";

// Unión de los valores que se guardan hoy en reservas.tipo_sesion: los que
// ofrece este contrato (tipo de evento) y los del formulario de /reserva
// (formato de sesión).
const TIPOS_SESION = [
  "Boda",
  "Xv años",
  "Aniversario",
  "Corporativo",
  "Individual",
  "Pareja",
  "Grupo",
  "Consulta / Asesoramiento",
  "Otro",
];

const formatMoney = (n) =>
  `$${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatFechaLarga = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" });
};

/**
 * Convierte las filas de `ordenes` de un mismo pedido en las líneas de detalle
 * del contrato. El checkout inserta UNA fila por item del carrito
 * (Compra.jsx), todas con el mismo `pedido_id`.
 */
const filasAPedido = (filas) => {
  const pedido = [];
  const agregados = new Map();

  const agregar = (clave, nombre, descripcion, cantidad, unitario) => {
    const cantidadTotal = Number(cantidad) || 1;
    if (agregados.has(clave)) {
      // Mismo producto repetido en varias filas: se acumula la cantidad.
      const actual = agregados.get(clave);
      actual.cantidad += cantidadTotal;
    } else {
      const linea = {
        nombre,
        descripcion,
        cantidad: cantidadTotal,
        precio: Number(unitario) || 0,
      };
      agregados.set(clave, linea);
      pedido.push(linea);
    }
  };

  filas.forEach((f) => {
    // Cada fila guarda el precio unitario de SU combo (servicio + paquete +
    // adicional). El detalle se arma por tipo para que el contrato enumere
    // cada cosa y su precio real.
    const combo = Number(f.precio_unitario) || 0;

    if (f.servicios) {
      const propio = Number(f.servicios.precio) || 0;
      agregar(
        `servicio:${f.servicios.id}`,
        f.servicios.nombre,
        f.servicios.descripcion,
        f.cantidad,
        propio || combo
      );
    }

    if (f.paquetes) {
      const propio = Number(f.paquetes.precio) || 0;
      agregar(
        `paquete:${f.paquetes.id}`,
        `Paquete · ${f.paquetes.nombre}`,
        f.paquetes.descripcion,
        f.cantidad,
        propio || combo
      );
    }

    if (f.adicional) {
      const propio = Number(f.adicional.precio) || 0;
      agregar(
        `adicional:${f.adicional.id}`,
        `Adicional · ${f.adicional.nombre}`,
        f.adicional.descripcion,
        f.cantidad,
        propio || combo
      );
    }
  });

  return pedido;
};

const Contrato = () => {
  const [user, setUser] = useState(null);
  const [order, setOrder] = useState(null);
  const [reserva, setReserva] = useState(null);
  const [items, setItems] = useState([]);
  const [pedidoTotal, setPedidoTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Paso 1: datos del contrato
  const [form, setForm] = useState({
    nombre: "",
    dni: "",
    telefono: "",
    direccion: "",
    fecha_evento: "",
    tipo_sesion: "",
  });
  const [saving, setSaving] = useState(false);
  const [formMsg, setFormMsg] = useState(null);

  // Paso 3: videollamada
  const [videollamada, setVideollamada] = useState({ modalidad: "Virtual", dia: "", horario: "" });
  const [guardandoVideollamada, setGuardandoVideollamada] = useState(false);
  const [videollamadaMsg, setVideollamadaMsg] = useState(null);

  const [pdfUrl, setPdfUrl] = useState(null);

  /* ------------------ carga inicial ------------------ */
  useEffect(() => {
    let activo = true;

    const cargar = async () => {
      try {
        const { data: { user: u }, error: userErr } = await supabase.auth.getUser();
        if (userErr || !u) throw new Error("Debes iniciar sesión");
        if (!activo) return;
        setUser(u);

        const CAMPOS_ORDEN = `
            id, usuario_id, pedido_id, fecha_creacion, montoTotal, metodoPago, estado,
            cantidad, precio_unitario,
            servicio_id, paquete_id, adicional_id
        `;

        // 1) La orden mas reciente del usuario.
        const { data: ordenData, error: ordErr } = await supabase
          .from("ordenes")
          .select(CAMPOS_ORDEN)
          .eq("usuario_id", u.id)
          .order("fecha_creacion", { ascending: false })
          .limit(1);

        if (ordErr) throw ordErr;
        if (!activo) return;

        const base = ordenData && ordenData.length ? ordenData[0] : null;

        // 2) Todas las filas del MISMO pedido. El checkout inserta una fila por item
        //    del carrito, asi que leer solo `base` hacia que el contrato no coincida
        //    con la compra. `pedido_id` es el grupo; las ordenes anteriores a esta
        //    columna (pedido_id null) caen a la fila mas reciente.
        let filas = base ? [base] : [];
        if (base?.pedido_id) {
          const { data: pedidoData, error: pedErr } = await supabase
            .from("ordenes")
            .select(CAMPOS_ORDEN)
            .eq("usuario_id", u.id)
            .eq("pedido_id", base.pedido_id)
            .order("fecha_creacion", { ascending: true });

          if (pedErr) {
            console.warn("[Contrato] No se pudo leer el pedido completo:", pedErr.message);
          } else if (pedidoData && pedidoData.length) {
            filas = pedidoData;
          }
        }

        // Resolvemos servicios/paquetes/adicional por id, en paralelo y en una sola
        // consulta por tabla: el detalle no depende de la config de embeds de
        // PostgREST y cubre TODAS las filas del pedido, no solo una.
        const buscarPorId = async (tabla, id, columnas = "id, nombre, descripcion, precio") => {
          if (id == null) return null;
          const { data, error } = await supabase
            .from(tabla)
            .select(columnas)
            .eq("id", id)
            .maybeSingle();
          if (error) {
            console.warn(`[Contrato] No se pudo leer ${tabla} (id=${id}):`, error.message);
            return null;
          }
          return data || null;
        };

        const pedirPorIds = async (tabla, ids, columnas = "id, nombre, descripcion, precio") => {
          if (!ids.length) return new Map();
          const { data, error } = await supabase.from(tabla).select(columnas).in("id", ids);
          if (error) {
            console.warn(`[Contrato] No se pudieron leer ${tabla}:`, error.message);
            return new Map();
          }
          return new Map((data || []).map((r) => [r.id, r]));
        };

        const idsUnicos = (campo) => [
          ...new Set(filas.map((f) => f[campo]).filter((v) => v != null)),
        ];

        const [serviciosMap, paquetesMap, adicionalesMap, usuarioOrden] = await Promise.all([
          pedirPorIds("servicios", idsUnicos("servicio_id")),
          pedirPorIds("paquetes", idsUnicos("paquete_id")),
          pedirPorIds("adicional", idsUnicos("adicional_id")),
          buscarPorId("usuarios", base?.usuario_id, "id, nombre, email, dni, telefono, direccion"),
        ]);

        if (!activo) return;

        const filasHydratadas = filas.map((f) => ({
          ...f,
          servicios: serviciosMap.get(f.servicio_id) || null,
          paquetes: paquetesMap.get(f.paquete_id) || null,
          adicional: adicionalesMap.get(f.adicional_id) || null,
        }));

        const principal = filasHydratadas[0] || null;
        const ord = principal ? { ...principal, usuarios: usuarioOrden } : null;
        setOrder(ord);

        // Detalle del pedido completo y total real (suma de todas sus filas).
        setItems(filasAPedido(filasHydratadas));
        setPedidoTotal(
          filasHydratadas.reduce((acc, f) => acc + (Number(f.montoTotal) || 0), 0)
        );

        // Perfil
        const { data: perfil } = await supabase
          .from("usuarios")
          .select("nombre, email, dni, telefono, direccion")
          .eq("id", u.id)
          .maybeSingle();
        if (!activo) return;

        // Reserva linked (para completar datos y reunión creativa)
        const { data: reservasData } = await supabase
          .from("reservas")
          .select("id, dia, horario, tipo_sesion, direccion, preferencias, observaciones, estado, created_at, videollamada_modalidad, videollamada_dia, videollamada_horario, videollamada_estado")
          .eq("usuario_id", u.id)
          .neq("estado", "cancelado")
          .order("created_at", { ascending: false })
          .limit(1);

        const res = reservasData && reservasData.length ? reservasData[0] : null;
        if (!activo) return;
        setReserva(res);

        const usr = ord?.usuarios || perfil || {};
        setForm({
          nombre: usr.nombre || "",
          dni: usr.dni || "",
          telefono: usr.telefono || ord?.usuarios?.telefono || "",
          direccion: res?.direccion || usr.direccion || "",
          fecha_evento: res?.dia || "",
          tipo_sesion: res?.tipo_sesion || "",
        });

        // La videollamada vive en columnas propias de la reserva: `dia` y
        // `horario` son la fecha del evento y no se pisan.
        setVideollamada({
          modalidad: res?.videollamada_modalidad || "Virtual",
          dia: res?.videollamada_dia || "",
          horario: res?.videollamada_horario || "",
        });

        setError(null);
      } catch (err) {
        console.error(err);
        if (activo) setError(err.message);
      } finally {
        if (activo) setLoading(false);
      }
    };

    cargar();
    return () => { activo = false; };
  }, []);

  const faltanDatos = useMemo(
    () => !form.nombre || !form.dni || !form.telefono || !form.direccion || !form.fecha_evento,
    [form]
  );

  /* ------------------ paso 1: guardar datos ------------------ */
  const guardarDatos = async (e) => {
    e.preventDefault();
    setFormMsg(null);

    if (!form.nombre || !form.dni || !form.telefono || !form.direccion || !form.fecha_evento) {
      setFormMsg({ type: "error", text: "Completá todos los campos para generar el contrato." });
      return;
    }

    setSaving(true);
    try {
      const { error: updErr } = await supabase
        .from("usuarios")
        .update({
          nombre: form.nombre,
          dni: form.dni,
          telefono: form.telefono,
          direccion: form.direccion,
        })
        .eq("id", user.id);
      if (updErr) throw updErr;

      // Si hay reserva, la actualizamos con fecha/lugar definitivos
      if (reserva?.id) {
        const { error: resErr } = await supabase
          .from("reservas")
          .update({
            dia: form.fecha_evento,
            direccion: form.direccion,
            tipo_sesion: form.tipo_sesion || reserva.tipo_sesion || null,
            telefono: form.telefono,
          })
          .eq("id", reserva.id);
        if (resErr) throw resErr;
        setReserva((prev) => ({ ...prev, dia: form.fecha_evento, direccion: form.direccion }));
      }

      // Refrescamos el usuario embebido de la orden para el PDF
      if (order) {
        setOrder((prev) => ({
          ...prev,
          usuarios: {
            ...(prev.usuarios || {}),
            nombre: form.nombre,
            dni: form.dni,
            telefono: form.telefono,
            direccion: form.direccion,
          },
        }));
      }

      setFormMsg({ type: "success", text: "Datos guardados. Tu contrato está listo." });
    } catch (err) {
      console.error(err);
      setFormMsg({ type: "error", text: err.message || "No se pudieron guardar los datos." });
    } finally {
      setSaving(false);
    }
  };

  /* ------------------ paso 3: videollamada con Sole ------------------ */
  const guardarVideollamada = async (e) => {
    e.preventDefault();
    setVideollamadaMsg(null);

    if (!videollamada.dia || !videollamada.horario) {
      setVideollamadaMsg({ type: "error", text: "Elegí día y horario para la videollamada." });
      return;
    }

    setGuardandoVideollamada(true);
    try {
      if (!reserva?.id) {
        // Sin reserva no hay dónde guardar el pedido de videollamada: avisar en
        // vez de mostrar un "listo" que nadie va a ver en el panel.
        setVideollamadaMsg({
          type: "error",
          text:
            "No encontré una reserva tuya para dejar la videollamada. Escribinos por WhatsApp y la coordinamos.",
        });
        return;
      }

      const { error: resErr } = await supabase
        .from("reservas")
        .update({
          videollamada_modalidad: videollamada.modalidad,
          videollamada_dia: videollamada.dia,
          videollamada_horario: videollamada.horario,
          videollamada_estado: "pendiente",
        })
        .eq("id", reserva.id);
      if (resErr) throw resErr;
      setReserva((prev) => ({
        ...prev,
        videollamada_modalidad: videollamada.modalidad,
        videollamada_dia: videollamada.dia,
        videollamada_horario: videollamada.horario,
        videollamada_estado: "pendiente",
      }));

      setVideollamadaMsg({
        type: "success",
        text:
          "Listo. Te contactamos por WhatsApp para confirmar la videollamada con Sole.",
      });
    } catch (err) {
      console.error(err);
      setVideollamadaMsg({ type: "error", text: err.message || "No se pudo guardar la videollamada." });
    } finally {
      setGuardandoVideollamada(false);
    }
  };

  const enlaceWhatsApp = useMemo(() => {
    if (!user) return "#";
    const d = videollamada.dia ? formatFechaLarga(videollamada.dia) || videollamada.dia : "a coordinar";
    const hora = videollamada.horario || "a coordinar";
    const pedidoRef = order?.pedido_id
      ? `pedido ${order.pedido_id.slice(0, 8)}`
      : `orden #${order?.id ?? "-"}`;
    const detalleCompra = items.length
      ? `\nCompra: ${items.map((it) => `${it.nombre} x${it.cantidad}`).join(", ")}.`
      : "";
    const texto =
      `¡Hola Sole! Soy ${form.nombre || "el cliente"}. Acabo de completar los datos de mi contrato ` +
      `(${pedidoRef}). Me gustaría coordinar una videollamada ${videollamada.modalidad.toLowerCase()} ` +
      `para el ${d} a las ${hora}.${detalleCompra}`;
    return `https://wa.me/${WHATSAPP_SOLE}?text=${encodeURIComponent(texto)}`;
  }, [user, videollamada, form.nombre, order, items]);

  /* ------------------ generación del PDF ------------------ */
  useEffect(() => {
    if (!order || faltanDatos) {
      setPdfUrl(null);
      return undefined;
    }

    const addMultiLineText = (doc, text, x, y, maxWidth, lineHeight) => {
      const lines = doc.splitTextToSize(text, maxWidth);
      lines.forEach((line) => {
        if (y > 750) {
          doc.addPage();
          y = 60;
        }
        doc.text(line, x, y);
        y += lineHeight;
      });
      return y;
    };

    const addTitle = (doc, title, x, y, lineHeight) => {
      doc.setFont(undefined, "bold");
      doc.text(title, x, y);
      doc.setFont(undefined, "normal");
      return y + lineHeight;
    };

    const generatePdfBlob = () => {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const m = 40;
      let y = 60;
      const lineHeight = 18;

      const contractor = "Costilla María Soledad";
      const contractorDNI = "33.966.161";
      const contractorAddr = "Bolivia 83, Yerba Buena, Tucumán";

      const clientName = form.nombre;
      const clientDNI = form.dni ? `DNI ${form.dni}` : "DNI __________";
      const clientPhone = form.telefono ? `Tel. ${form.telefono}` : "";
      const clientMail = order.usuarios?.email ? ` — ${order.usuarios.email}` : "";
      const eventDate = form.fecha_evento ? formatFechaLarga(form.fecha_evento) : "____ de ______ de ______";
      const eventPlace = form.direccion;
      const payMethod = order.metodoPago || "a coordinar";

      // ---------------- TÍTULO ----------------
      // El encabezado nombra lo principal que se contrató (el primer servicio o
      // paquete del pedido), no solo el de la fila que se leyó.
      const nombrePrincipal =
        items.find((it) => !it.nombre.startsWith("Paquete ·") && !it.nombre.startsWith("Adicional ·"))
          ?.nombre || items[0]?.nombre;

      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text(`CONTRATO DE OBRAS Y SERVICIOS - ${nombrePrincipal || "Servicio contratado"}`, m, y, { maxWidth: 620, align: "justify" });
      y += 40;

      doc.setFontSize(12);
      doc.setFont(undefined, "normal");
      const intro =
`Entre ${contractor}, DNI ${contractorDNI}, domiciliado en ${contractorAddr}, en adelante "El Contratista",
y ${clientName} (${clientDNI}${clientPhone ? `, ${clientPhone}` : ""}${clientMail}), en adelante "El Cliente", convienen celebrar el presente contrato de obras y servicios conforme a las cláusulas que se detallan a continuación:`;
      y = addMultiLineText(doc, intro, m, y, 520, lineHeight);
      y += 30;

      // ---------------- DETALLE ----------------
      const detalle = [
        {
          title: "PRIMERA. DETALLE DEL EVENTO, LUGAR Y FECHA:",
          text:
            `Fecha del evento: ${eventDate}\n` +
            `Lugar: ${eventPlace}` +
            (form.tipo_sesion ? `\nTipo de sesión: ${form.tipo_sesion}` : ""),
        },
      ];

      // ---------------- PRECIO ----------------
      // Total del pedido completo: suma de `montoTotal` de todas sus filas, con
      // respaldo en el detalle si somehow no viene de la base.
      const total = pedidoTotal || items.reduce((acc, it) => acc + it.precio * it.cantidad, 0);

      detalle.push({
        title: "SEGUNDA. PRECIO:",
        text: `Total del servicio contratado: ${formatMoney(total)}, pagaderos mediante ${payMethod}. Se abonará seña de ${formatMoney(total * 0.5)} y saldo restante de ${formatMoney(total * 0.5)} a fecha ${form.fecha_evento ? formatFechaLarga(form.fecha_evento) : "____/____/______"}.`,
      });

      detalle.push({
        title: "TERCERA. EL PRECIO INCLUYE:",
        text: items.length
          ? items.map((it) => `• ${it.nombre}${it.cantidad > 1 ? ` (x${it.cantidad})` : ""} — ${formatMoney(it.precio * it.cantidad)}`).join("\n")
          : "Los servicios detallados en la orden de compra.",
      });

      detalle.push({ title: "CUARTA. RESERVACIÓN:", text: "La firma del contrato y el anticipo son requeridos para reservar la fecha del evento." });

      if (reserva) {
        detalle.push({
          title: "QUINTA. REUNIÓN CREATIVA PREVIA:",
          text:
`Las partes acordan una reunión creativa previa (${videollamada.modalidad.toLowerCase()}) ` +
`para definir la idea, las referencias y el cronograma de la producción. ` +
`Fecha propuesta: ${formatFechaLarga(videollamada.dia) || "a coordinar"} a las ${videollamada.horario || "a coordinar"}. ` +
`La misma se realizará mediante videollamada o encuentro presencial, según lo coordinate El Contratista.`,
        });
      }

      detalle.push({ title: "SEXTA. COOPERACIÓN:", text: "El Cliente se compromete a proporcionar cooperación y comunicación para un resultado óptimo." });
      detalle.push({ title: "SÉPTIMA. TIEMPO DEDICADO:", text: "El/los Fotógrafo/s asistirá/n en la fecha y horas pactadas, cubriendo la esencia del evento." });
      detalle.push({ title: "OCTAVA. REGLAS DE CASA:", text: "El/los Fotógrafo/s estará/n limitado/s a las reglas del lugar del evento, Iglesia o recepción." });
      detalle.push({ title: "NOVENA. LÍMITE DE RESPONSABILIDAD:", text: "Si por fuerza mayor no pudiera/n cumplir, se buscará reemplazo profesional y se informará al Cliente." });
      detalle.push({ title: "DÉCIMA. ANTICIPO DE SEGURIDAD:", text: "Si se cancela el evento, el anticipo no será devuelto. El cambio de fecha queda sujeto a disponibilidad." });
      detalle.push({ title: "DÉCIMA PRIMERA. TIEMPO DE ENTREGA:", text: "Entrega del trabajo aproximadamente 60 días hábiles tras el evento. Entrega anticipada con cargo extra." });
      detalle.push({ title: "DÉCIMA SEGUNDA. LEY APLICABLE:", text: "Se rige por los artículos 1.251 a 1.277 de la Ley 26.994 (Código Civil y Comercial)." });
      detalle.push({ title: "DÉCIMA TERCERA. TRIBUNALES COMPETENTES:", text: "Las partes se someten a la jurisdicción de los tribunales de San Miguel de Tucumán." });

      detalle.forEach((cl) => {
        y = addTitle(doc, cl.title, m, y, lineHeight);
        y = addMultiLineText(doc, cl.text, m + 10, y, 500, lineHeight);
        y += 20;
      });

      // ---------------- NOTAS ----------------
      const notes = [
        "La comida de los participantes debe estar cubierta.",
        "En caso de disturbios, el equipo se retira sin responsabilidad.",
        "Viáticos no incluidos, se cotizan el mismo día.",
        "Las fotos podrían usarse con fines publicitarios; de lo contrario se abonará extra.",
        "Avisar al personal del salón de no utilizar máquina de humo o láser.",
        "El trabajo debe estar abonado siete días antes del evento; de no ser así no se presenta el equipo.",
        "El Cliente debe resguardar la información obtenida; el fotógrafo borra todo el material tras la entrega.",
      ];

      y = addTitle(doc, "Notas:", m, y, lineHeight);
      notes.forEach((note) => {
        y = addMultiLineText(doc, `- ${note}`, m + 10, y, 500, lineHeight);
        y += 5;
      });

      // ---------------- FIRMA ----------------
      const dateStr = new Date(order.fecha_creacion).toLocaleDateString("es-AR");
      y += 30;
      y = addMultiLineText(doc, `En San Miguel de Tucumán, a los ${dateStr}.\nSe firman dos ejemplares del mismo tenor y a un solo efecto.`, m, y, 520, lineHeight);

      y += 60;
      doc.line(m, y, m + 220, y);
      doc.text("Firma del Cliente", m, y + 15);
      doc.line(m + 300, y, m + 520, y);
      doc.text("Firma del Contratista", m + 300, y + 15);

      return doc.output("blob");
    };

    const blob = generatePdfBlob();
    const url = URL.createObjectURL(blob);
    setPdfUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [order, items, pedidoTotal, reserva, form, faltanDatos, videollamada.modalidad, videollamada.dia, videollamada.horario]);

  /* ------------------ render ------------------ */
  if (loading) {
    return (
      <>
        <div className="contract-container">
          <div className="contract-state contract-state--loading">Cargando tu contrato…</div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="contract-container">
          <div className="contract-state contract-state--error" role="alert">{error}</div>
        </div>
        <Footer />
      </>
    );
  }

  if (!order) {
    return (
      <>
        <div className="contract-container">
          <header className="contract-header">
            <h1 className="contract-title">Mi Contrato</h1>
          </header>
          <div className="contract-empty">
            <h3>No tenés órdenes registradas</h3>
            <p>Una vez que compres un paquete, podrás ver tu contrato aquí.</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
    <div className="contract-container">
      <header className="contract-header">
        <h1 className="contract-title">Mi Contrato</h1>
        <p className="contract-subtitle">Orden #{order.id}</p>
      </header>

      {/* ========== PASO 1 · Datos del contrato ========== */}
      <section className="contract-card">
        <p className="contract-step">Paso 1 de 3</p>
        <h2 className="contract-card-title">Completá tus datos</h2>
        <p className="contract-card-text">
          Necesitamos estos datos para firmar el contrato. Los guardamos en tu perfil para que no vuelvas a cargarlos.
        </p>

        <form className="contract-form" onSubmit={guardarDatos} noValidate>
          <div className="contract-field">
            <label htmlFor="c-nombre">Nombre y apellido</label>
            <input id="c-nombre" type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Nombre y apellido" />
          </div>

          <div className="contract-field">
            <label htmlFor="c-dni">DNI</label>
            <input id="c-dni" type="text" inputMode="numeric" value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} placeholder="12345678" />
          </div>

          <div className="contract-field">
            <label htmlFor="c-telefono">Teléfono / WhatsApp</label>
            <input id="c-telefono" type="tel" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="381 000 0000" />
          </div>

          <div className="contract-field contract-field--full">
            <label htmlFor="c-direccion">Dirección del evento</label>
            <input id="c-direccion" type="text" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="Calle, número, barrio, localidad" />
          </div>

          <div className="contract-field">
            <label htmlFor="c-fecha">Fecha del evento</label>
            <input id="c-fecha" type="date" value={form.fecha_evento} onChange={(e) => setForm({ ...form, fecha_evento: e.target.value })} />
          </div>

          <div className="contract-field">
            <label htmlFor="c-tipo">Tipo de sesión</label>
            <select id="c-tipo" value={form.tipo_sesion} onChange={(e) => setForm({ ...form, tipo_sesion: e.target.value })}>
              <option value="">Elegí una opción</option>
              {/* La columna reservas.tipo_sesion viene con dos vocabularios: el que
                  carga /reserva (formato de sesión) y el de acá (tipo de evento).
                  Cualquier valor que no esté en la lista se agrega como opción para
                  que la selección nunca aparezca en blanco ni se pierda al guardar. */}
              {form.tipo_sesion && !TIPOS_SESION.includes(form.tipo_sesion) && (
                <option value={form.tipo_sesion}>{form.tipo_sesion}</option>
              )}
              {TIPOS_SESION.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {formMsg && <p className={`contract-msg contract-msg--${formMsg.type}`} role="status">{formMsg.text}</p>}

          <button type="submit" className="contract-submit" disabled={saving}>
            {saving ? "Guardando…" : "Guardar datos y generar contrato"}
          </button>
        </form>
      </section>

      {/* ========== PASO 2 · Contrato ========== */}
      {pdfUrl ? (
        <section className="contract-card">
          <p className="contract-step">Paso 2 de 3</p>
          <h2 className="contract-card-title">Tu contrato está listo</h2>
          <p className="contract-card-text">Descargalo, revisalo y firmalo. Después coordinamos la videollamada.</p>
          <iframe src={pdfUrl} className="contract-iframe" title="Contrato" />
          <a href={pdfUrl} download={`contrato_orden_${order.id}.pdf`} className="download-btn">
            Descargar Contrato
          </a>
        </section>
      ) : (
        <div className="contract-hint">
          {faltanDatos
            ? "Completá tus datos arriba para generar el contrato."
            : "Generando contrato…"}
        </div>
      )}

      {/* ========== PASO 3 · Videollamada con Sole ========== */}
      {pdfUrl && (
        <section className="contract-card">
          <p className="contract-step">Paso 3 de 3</p>
          <h2 className="contract-card-title">Coordiná tu videollamada con Sole</h2>
          <p className="contract-card-text">
            Último paso: elegí cuándo te viene bien y confirmamos por WhatsApp. En la videollamada definimos
            la idea, las referencias y el cronograma.
          </p>

          <form className="contract-form" onSubmit={guardarVideollamada} noValidate>
            <div className="contract-field contract-field--full">
              <label htmlFor="v-modalidad">Modalidad</label>
              <select id="v-modalidad" value={videollamada.modalidad} onChange={(e) => setVideollamada({ ...videollamada, modalidad: e.target.value })}>
                <option value="Virtual">Videollamada (Google Meet / Zoom)</option>
                <option value="Presencial">Presencial</option>
              </select>
            </div>

            <div className="contract-field">
              <label htmlFor="v-dia">Día</label>
              <input id="v-dia" type="date" value={videollamada.dia} onChange={(e) => setVideollamada({ ...videollamada, dia: e.target.value })} />
            </div>

            <div className="contract-field">
              <label htmlFor="v-horario">Horario</label>
              <select id="v-horario" value={videollamada.horario} onChange={(e) => setVideollamada({ ...videollamada, horario: e.target.value })}>
                <option value="">Elegí un horario</option>
                {["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "19:00"].map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>

            {videollamadaMsg && (
              <p className={`contract-msg contract-msg--${videollamadaMsg.type}`} role="status">{videollamadaMsg.text}</p>
            )}

            <button type="submit" className="contract-submit" disabled={guardandoVideollamada}>
              {guardandoVideollamada ? "Guardando…" : "Confirmar videollamada"}
            </button>
          </form>

          <a href={enlaceWhatsApp} target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
            Coordinar videollamada con Sole por WhatsApp
          </a>
        </section>
      )}
    </div>
    <Footer />
    </>
  );
};

export default Contrato;